import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ScenarioInput } from '../types';
import * as api from '../utils/api';

type BusyKey = 'scenarioJson' | 'resultsJson' | 'resultsCsv' | 'import';

interface Props {
  /** When provided, Export Scenario JSON fetches from the saved API record */
  scenarioId?: string | number | null;
  /** Current scenario state — used for inline JSON export and results export */
  currentScenario: ScenarioInput;
  /** When provided, called instead of the default create+navigate flow on import */
  onImport?: (json: ScenarioInput) => Promise<void>;
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function safeName(text: string): string {
  return text.replace(/[^a-z0-9-_]/gi, '_').slice(0, 60);
}

function SpinIcon() {
  return (
    <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
    </svg>
  );
}

export default function ImportExportBar({ scenarioId, currentScenario, onImport }: Props) {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [busy, setBusy] = useState<BusyKey | null>(null);
  const [toast, setToast] = useState<{ msg: string; error: boolean } | null>(null);

  function showToast(msg: string, error = false) {
    setToast({ msg, error });
    setTimeout(() => setToast(null), 3000);
  }

  async function handleExportScenarioJson() {
    setBusy('scenarioJson');
    try {
      const name = safeName(currentScenario.name || 'scenario');
      if (scenarioId != null) {
        const blob = await api.exportScenarioJson(scenarioId);
        downloadBlob(blob, `scenario_${name}_${scenarioId}.json`);
      } else {
        // Export directly from frontend store (unsaved scenario)
        const json = JSON.stringify(currentScenario, null, 2);
        const blob = new Blob([json], { type: 'application/json' });
        downloadBlob(blob, `scenario_${name}.json`);
      }
      showToast('Scenario JSON downloaded');
    } catch (e) {
      showToast((e as Error).message || 'Export failed', true);
    } finally {
      setBusy(null);
    }
  }

  async function handleExportResultsJson() {
    setBusy('resultsJson');
    try {
      const blob = await api.exportResultsJson(currentScenario);
      const name = safeName(currentScenario.name || 'results');
      downloadBlob(blob, `results_${name}.json`);
      showToast('Results JSON downloaded');
    } catch (e) {
      showToast((e as Error).message || 'Export failed', true);
    } finally {
      setBusy(null);
    }
  }

  async function handleExportResultsCsv() {
    setBusy('resultsCsv');
    try {
      const blob = await api.exportResultsCsv(currentScenario);
      const name = safeName(currentScenario.name || 'results');
      downloadBlob(blob, `results_${name}.csv`);
      showToast('Results CSV downloaded');
    } catch (e) {
      showToast((e as Error).message || 'Export failed', true);
    } finally {
      setBusy(null);
    }
  }

  async function handleImportFile(file: File) {
    setBusy('import');
    try {
      const text = await file.text();
      const json = JSON.parse(text) as ScenarioInput;
      if (onImport) {
        await onImport(json);
      } else {
        const res = await api.createScenario(json);
        navigate(`/scenario/${res.id}`);
      }
      showToast('Scenario imported');
    } catch (e) {
      showToast((e as Error).message || 'Import failed', true);
    } finally {
      setBusy(null);
    }
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) handleImportFile(file);
    e.target.value = ''; // reset so same file can be re-selected
  }

  const isBusy = busy !== null;

  return (
    <div className="relative">
      <div className="cnc-panel flex flex-wrap items-center gap-2 rounded-xl px-4 py-3">
        <span className="text-xs font-semibold uppercase tracking-widest text-slate-500 mr-1 shrink-0">
          Export / Import
        </span>

        {/* Export Scenario JSON */}
        <button
          onClick={handleExportScenarioJson}
          disabled={isBusy}
          className="btn-secondary inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg"
        >
          {busy === 'scenarioJson' ? (
            <SpinIcon />
          ) : (
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
          )}
          Export Scenario JSON
        </button>

        {/* Export Results JSON */}
        <button
          onClick={handleExportResultsJson}
          disabled={isBusy || currentScenario.vehicles.length === 0}
          className="btn-secondary inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg"
        >
          {busy === 'resultsJson' ? (
            <SpinIcon />
          ) : (
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          )}
          Export Results JSON
        </button>

        {/* Export Results CSV */}
        <button
          onClick={handleExportResultsCsv}
          disabled={isBusy || currentScenario.vehicles.length === 0}
          className="btn-secondary inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg"
        >
          {busy === 'resultsCsv' ? (
            <SpinIcon />
          ) : (
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          )}
          Export Results CSV
        </button>

        {/* Import Scenario */}
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={isBusy}
          className="btn-secondary inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg"
        >
          {busy === 'import' ? (
            <SpinIcon />
          ) : (
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l4-4m0 0l4 4m-4-4v12" />
            </svg>
          )}
          Import Scenario
        </button>

        <input
          ref={fileInputRef}
          type="file"
          accept=".json,application/json"
          className="hidden"
          onChange={handleFileChange}
        />
      </div>

      {/* Toast notification */}
      {toast && (
        <div
          className={`absolute top-full left-0 mt-2 px-3 py-2 rounded-lg text-xs font-medium z-10 shadow-lg whitespace-nowrap ${
            toast.error
              ? 'bg-red-900/90 border border-red-700 text-red-200'
              : 'bg-emerald-900/90 border border-emerald-700 text-emerald-200'
          }`}
        >
          {toast.error ? '✕ ' : '✓ '}
          {toast.msg}
        </div>
      )}
    </div>
  );
}
